import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Container,
  Divider,
  Group,
  List,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconCheck, IconCircle, IconPlugConnected, IconWifi, IconX } from "@tabler/icons-react";
import {
  connectADB,
  detectHardwareModel,
  disconnectADB,
  enableDpvrKioskMode,
  grantPermissions,
  installApk,
  isAdbConnected,
  populateDeviceInfo,
  pushConfig,
  pushConfig1,
  rebootDevice,
  getSerial,
  verifyInstalledVersion,
} from "@utils/webusb-utils/ADBUtils";
import {
  pushesPicoLauncherConfig,
  pushesPicoSeethroughConfig,
} from "@utils/webusb-utils/headsetSetupSteps";
import { getDefaultVersion } from "@utils/api-communication/HeadsetReleasesCli";
import { downloadReleaseApk } from "@utils/webusb-utils/releaseApkDownload";
import { HardwareModels } from "types/productTypes";
import { hardwareNameOf } from "types/headsetReleaseTypes";
import { useOptionalAdbConnection } from "@components/wifi_setup/AdbConnectionContext";
import { showPermissionDialogsNotice } from "@utils/permissionDialogsNotice";
import DownloadProgressBar, { DownloadProgressState } from "./DownloadProgressBar";

type StepKey = "connect" | "download" | "install" | "verify" | "config" | "restart" | "done";
type StepStatus = "pending" | "active" | "success" | "error";

const STEP_ORDER: StepKey[] = [
  "connect",
  "download",
  "install",
  "verify",
  "config",
  "restart",
  "done",
];

const STEP_LABEL_KEY: Record<StepKey, string> = {
  connect: "Setup.stepConnect",
  download: "Setup.stepDownload",
  install: "Setup.stepInstall",
  verify: "Setup.stepVerify",
  config: "Setup.stepConfig",
  restart: "Setup.stepRestart",
  done: "Setup.stepDone",
};

const DISCONNECT_ERROR_PATTERN =
  /disconnect|no device|device (?:was )?(?:lost|removed|unplugged|gone)|device unavailable|transfer(?:in|out)?|networkerror|not connected|connect to device first/i;

const isCableDisconnectError = (e: any): boolean => {
  const name = (e?.name || "").toString().toLowerCase();
  const msg = (e?.message || String(e || "")).toLowerCase();
  return name === "networkerror" || name === "notfounderror" || DISCONNECT_ERROR_PATTERN.test(msg);
};

const initialStatuses = (): Record<StepKey, StepStatus> =>
  STEP_ORDER.reduce(
    (acc, key) => ({ ...acc, [key]: "pending" }),
    {} as Record<StepKey, StepStatus>,
  );

interface RecoveryWithWebUsbProps {
  embedded?: boolean;
}

const RecoveryWithWebUsb: React.FC<RecoveryWithWebUsbProps> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sharedConnection = useOptionalAdbConnection();
  const dispatch = useDispatch();
  const [connected, setConnected] = useState(false);
  const [modelInfo, setModelInfo] = useState<string>("-");
  const [serialInfo, setSerialInfo] = useState<string>("");
  const [logs, setLogs] = useState<string[]>([]);
  const [running, setRunning] = useState(false);
  const [statuses, setStatuses] = useState<Record<StepKey, StepStatus>>(initialStatuses);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgressState | null>(null);
  const currentStepRef = useRef<StepKey | null>(null);
  // The final reboot intentionally drops the USB link; suppress the
  // "cable disconnected" error for that expected disconnect.
  const expectDisconnectRef = useRef(false);

  const appendLog = (line: string) => setLogs((l) => [...l, line]);

  useEffect(() => {
    if (!sharedConnection) return;
    setConnected(sharedConnection.connected);
    setModelInfo(sharedConnection.connected ? sharedConnection.model || "-" : "-");
    setSerialInfo(sharedConnection.connected ? sharedConnection.serial : "");
  }, [
    sharedConnection,
    sharedConnection?.connected,
    sharedConnection?.model,
    sharedConnection?.serial,
  ]);

  // Another tool on the page may already hold the shared USB handle.
  useEffect(() => {
    if (sharedConnection || !isAdbConnected()) return;
    void (async () => {
      try {
        setModelInfo(await detectHardwareModel());
        setSerialInfo(getSerial() ?? "");
        setConnected(true);
        setLogs((l) => [...l, "Using the existing USB connection"]);
      } catch (e: any) {
        setLogs((l) => [...l, "Could not read the connected device: " + (e?.message || e)]);
      }
    })();
  }, []);

  useEffect(() => {
    const usb: any = (navigator as any).usb;
    if (!usb?.addEventListener) return;
    const handleUsbDisconnect = () => {
      if (expectDisconnectRef.current) return;
      setConnected(false);
      setErrorMsg(t("Setup.cableDisconnected"));
      setStatuses((s) => {
        const cur = currentStepRef.current;
        return cur && s[cur] === "active" ? { ...s, [cur]: "error" } : s;
      });
      if (!sharedConnection) disconnectADB();
    };
    usb.addEventListener("disconnect", handleUsbDisconnect);
    return () => usb.removeEventListener("disconnect", handleUsbDisconnect);
  }, [t, sharedConnection]);

  const logCb = (msg: string) => {
    appendLog(msg);
    if (msg.startsWith("Model:")) setModelInfo(msg.replace("Model:", "").trim());
    if (msg.startsWith("Serial:")) setSerialInfo(msg.replace("Serial:", "").trim());
  };

  const reportDownloadProgress = (loaded: number, total?: number) => {
    setDownloadProgress({ loaded, total });
  };

  const beginStep = (key: StepKey) => {
    currentStepRef.current = key;
    setStatuses((s) => ({ ...s, [key]: "active" }));
  };

  const completeStep = (key: StepKey) => {
    setStatuses((s) => ({ ...s, [key]: "success" }));
  };

  const handleConnect = async () => {
    setRunning(true);
    expectDisconnectRef.current = false;
    try {
      await connectADB(logCb);
      await populateDeviceInfo(logCb);
      setConnected(true);
      // Clear any earlier failure once we are actually connected.
      setErrorMsg(null);
      setStatuses(initialStatuses());
      currentStepRef.current = null;
    } catch (e: any) {
      appendLog("Connect error: " + (e?.message || e));
      setConnected(false);
      setErrorMsg(
        isCableDisconnectError(e)
          ? t("Setup.cableDisconnected")
          : e?.message || t("Setup.connectFailed"),
      );
    } finally {
      setRunning(false);
    }
  };

  const runRecovery = async () => {
    if (running) return;
    setRunning(true);
    setErrorMsg(null);
    setStatuses(initialStatuses());
    currentStepRef.current = null;
    expectDisconnectRef.current = false;

    try {
      // 1) Connect to the headset (reuse the existing session if already open).
      beginStep("connect");
      if (sharedConnection) {
        if (!sharedConnection.connected) await sharedConnection.connect();
        if (!isAdbConnected()) throw new Error(t("Setup.connectFailed"));
        appendLog("Using the shared USB connection");
      } else if (!isAdbConnected()) {
        await connectADB(logCb);
      } else {
        appendLog("Already connected to a device");
      }
      await populateDeviceInfo(logCb);
      setConnected(true);
      completeStep("connect");

      // 2) Resolve the org's default version and download the APK. Version
      // resolution is part of this step so a failure here is attributed to it.
      beginStep("download");
      const model = await detectHardwareModel();
      const defaultVersion = await getDefaultVersion(model).catch(() => {
        throw new Error(t("Setup.noVersion"));
      });
      if (!defaultVersion) {
        throw new Error(t("Setup.noVersionForHardware", { hardware: hardwareNameOf(model) }));
      }
      const { version } = defaultVersion;
      appendLog(`Default version for ${model}: ${version} (${defaultVersion.source})`);

      setDownloadProgress({ loaded: 0 });
      try {
        await downloadReleaseApk(version, model, logCb, reportDownloadProgress);
      } finally {
        setDownloadProgress(null);
      }
      completeStep("download");

      beginStep("install");
      await installApk(undefined, logCb);
      completeStep("install");

      beginStep("verify");
      await verifyInstalledVersion(version, logCb);
      completeStep("verify");

      // 5) Apply the required configuration.
      beginStep("config");
      if (model === HardwareModels.Dpvr) {
        await enableDpvrKioskMode(logCb);
      }
      if (pushesPicoLauncherConfig(model)) {
        await pushConfig(logCb);
      }
      if (pushesPicoSeethroughConfig(model)) {
        await pushConfig1(logCb);
      }
      await grantPermissions(logCb);
      completeStep("config");

      // 6) Restart the headset so the new build and config take effect.
      // The reboot drops the USB link on purpose, so don't treat that
      // disconnect as a cable error.
      beginStep("restart");
      expectDisconnectRef.current = true;
      await rebootDevice(logCb);
      setConnected(false);
      completeStep("restart");

      // 7) Done.
      beginStep("done");
      completeStep("done");
      showPermissionDialogsNotice(dispatch, t);
    } catch (e: any) {
      const disconnected = isCableDisconnectError(e);
      appendLog("Recovery error: " + (e?.message || e));
      setErrorMsg(disconnected ? t("Setup.cableDisconnected") : e?.message || String(e));
      if (disconnected) setConnected(false);
      const failedStep = currentStepRef.current;
      if (failedStep) {
        setStatuses((s) => ({ ...s, [failedStep]: "error" }));
      }
    } finally {
      setRunning(false);
    }
  };

  const renderStepIcon = (status: StepStatus) => {
    if (status === "active") return <Loader size={18} color="orange" />;
    if (status === "success") {
      return (
        <ThemeIcon size={20} radius="xl" color="green">
          <IconCheck size={14} />
        </ThemeIcon>
      );
    }
    if (status === "error") {
      return (
        <ThemeIcon size={20} radius="xl" color="red">
          <IconX size={14} />
        </ThemeIcon>
      );
    }
    return (
      <ThemeIcon size={20} radius="xl" variant="light" color="gray">
        <IconCircle size={14} />
      </ThemeIcon>
    );
  };

  const stepColor = (status: StepStatus) => {
    if (status === "active") return "var(--oculera-text)";
    if (status === "pending") return "var(--oculera-text-muted)";
    return undefined;
  };

  const allDone = statuses.done === "success";

  const content = (
    <Paper p="md" withBorder>
      <Stack gap="sm">
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="sm">
            <Title order={4}>{t("Setup.title")}</Title>
            <Badge color={connected ? "green" : "red"} size="lg">
              {connected ? t("Setup.statusConnected") : t("Setup.statusDisconnected")}
            </Badge>
          </Group>
          {!embedded && (
            <Button
              variant="light"
              onClick={() => navigate("/wifi-setup")}
              rightSection={<IconWifi size={16} />}
              data-testid="device-setup-wifi-setup-link"
            >
              {t("WifiSetupLink")}
            </Button>
          )}
        </Group>

        <Text size="sm" c="dimmed">
          {t("Setup.intro")}
        </Text>

        {!connected && (
          <Alert
            variant="light"
            color="blue"
            icon={<IconPlugConnected size={18} />}
            title={t("Setup.notConnectedTitle")}
          >
            {t("Setup.notConnectedMessage")}
          </Alert>
        )}

        <Divider />

        <Text>
          {t("Setup.deviceModel")}: {modelInfo}
          {serialInfo ? ` - ${serialInfo}` : ""}
        </Text>

        <Group>
          {!sharedConnection && (
            <Button variant="default" onClick={handleConnect} disabled={running}>
              {t("Setup.connect")}
            </Button>
          )}
          <Button onClick={runRecovery} disabled={running} loading={running}>
            {t("Setup.downloadLatest")}
          </Button>
        </Group>

        {(running || allDone || errorMsg) && (
          <Paper p="sm" withBorder>
            <List spacing="xs" size="sm" center>
              {STEP_ORDER.map((key) => (
                <List.Item key={key} icon={renderStepIcon(statuses[key])}>
                  <Text c={stepColor(statuses[key])}>{t(STEP_LABEL_KEY[key])}</Text>
                  {key === "download" && downloadProgress && (
                    <DownloadProgressBar {...downloadProgress} />
                  )}
                </List.Item>
              ))}
            </List>
          </Paper>
        )}

        {allDone && (
          <Text c="green" fw={600}>
            {t("Setup.completed")}
          </Text>
        )}

        {errorMsg && (
          <Paper p="sm" withBorder bg="var(--mantine-color-red-light)">
            <Text c="red" fw={600}>
              {t("Setup.failed")}
            </Text>
            <Text c="red" size="sm">
              {errorMsg}
            </Text>
          </Paper>
        )}

        <Divider />

        <Text>{t("Setup.logs")}</Text>
        <ScrollArea style={{ height: 220 }} scrollbarSize={10}>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
            {logs.length ? logs.join("\n") : t("Setup.noLogs")}
          </pre>
        </ScrollArea>
      </Stack>
    </Paper>
  );

  return embedded ? (
    content
  ) : (
    <Container size="sm" my="md">
      {content}
    </Container>
  );
};

export default RecoveryWithWebUsb;
